import { getToken } from "../api";
import { useState, useEffect, useRef } from "react";

const BASE = "https://clavex.objetivabroker.es/api";
const authFetch = (url, opts = {}) => {
  const token = getToken();
  return fetch(url, {
    ...opts,
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(opts.headers || {}) },
  });
};

const USERS = [
  { id: "6a1d68ea40b970a912044bda", name: "Javier Arquillo", avatar: "JA", color: "#8B6914" },
  { id: "6a1d68eb40b970a912044bdc", name: "Javier Segura",   avatar: "JS", color: "#8B6914" },
  { id: "6a1d68eb40b970a912044bdd", name: "Susana Pierre",   avatar: "SP", color: "#00ffe7" },
];

const EMOJIS = ["👍","👎","❤️","🔥","😂","👀","🎯","✅"];

const Icon = ({ d, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d={d} />
  </svg>
);

function Avatar({ avatar, color, size = 32, online }) {
  return (
    <div style={{ position: "relative", flexShrink: 0 }}>
      <div style={{
        width: size, height: size, borderRadius: "50%",
        background: "var(--lift)", border: "1px solid var(--goldDim)",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: size * 0.32, fontWeight: 700, color, letterSpacing: 0.5,
      }}>{avatar}</div>
      {online !== undefined && (
        <div style={{
          position: "absolute", bottom: 0, right: 0,
          width: 8, height: 8, borderRadius: "50%",
          background: online ? "#3A6B4A" : "var(--mute)",
          border: "2px solid var(--bg)",
        }} />
      )}
    </div>
  );
}

export default function Communications({ currentUser }) {
  const [channel, setChannel]           = useState("general");
  const [messages, setMessages]         = useState([]);
  const [online, setOnline]             = useState([]);
  const [text, setText]                 = useState("");
  const [sending, setSending]           = useState(false);
  const [dragging, setDragging]         = useState(false);
  const [typingUsers, setTypingUsers]   = useState([]);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mobilePane, setMobilePane]     = useState("list");
  const [isMobile, setIsMobile]         = useState(window.innerWidth <= 768);

  const wsRef       = useRef(null);
  const bottomRef   = useRef(null);
  const fileRef     = useRef(null);
  const inputRef    = useRef(null);
  const typingTimer = useRef(null);

  const userId = String(currentUser.id);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  useEffect(() => {
    const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
    const ws = new WebSocket(`${proto}//${window.location.host}/chat/ws/${userId}?token=${getToken() || ""}`);
    wsRef.current = ws;
    ws.onmessage = (e) => {
      const data = JSON.parse(e.data);
      if (data.type === "new_message") {
        const msg = data.message;
        const relevant =
          (msg.channel === "general" && channel === "general") ||
          (msg.channel !== "general" && (msg.from_id === userId || msg.to_id === userId));
        if (relevant) setMessages(prev => [...prev, msg]);
      }
      if (data.type === "typing") {
        const fromUser = USERS.find(u => u.id === data.from_id);
        if (!fromUser) return;
        if (data.is_typing) {
          setTypingUsers(prev => prev.includes(fromUser.name) ? prev : [...prev, fromUser.name]);
        } else {
          setTypingUsers(prev => prev.filter(n => n !== fromUser.name));
        }
      }
      if (data.type === "reaction") {
        setMessages(prev => prev.map(m => {
          if (m.id !== data.msg_id) return m;
          const reactions = { ...(m.reactions || {}) };
          if (!reactions[data.emoji]) reactions[data.emoji] = [];
          if (reactions[data.emoji].includes(data.from_id)) {
            reactions[data.emoji] = reactions[data.emoji].filter(id => id !== data.from_id);
            if (reactions[data.emoji].length === 0) delete reactions[data.emoji];
          } else {
            reactions[data.emoji] = [...reactions[data.emoji], data.from_id];
          }
          return { ...m, reactions };
        }));
      }
      if (data.type === "presence") fetchOnline();
    };
    ws.onopen = () => { setTimeout(fetchOnline, 300); };
    fetchOnline();
    const interval = setInterval(fetchOnline, 5000);
    return () => { ws.close(); clearInterval(interval); };
  }, [userId]);

  useEffect(() => { fetchMessages(); }, [channel]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, typingUsers]);

  const fetchMessages = async () => {
    try {
      const res = await authFetch(`${BASE}/chat/messages?channel=${channel}`, { credentials: "include" });
      setMessages(await res.json());
    } catch {}
  };

  const fetchOnline = async () => {
    try {
      const res = await authFetch(`${BASE}/chat/online`);
      const data = await res.json();
      setOnline(data.online || []);
    } catch {}
  };

  const sendTyping = async (isTyping) => {
    try {
      await authFetch(`${BASE}/chat/typing`, {
        method: "POST",
        body: JSON.stringify({ from_id: userId, to_id: channel, is_typing: isTyping }),
      });
    } catch {}
  };

  const handleTextChange = (e) => {
    const val = e.target.value;
    setText(val);
    const lastAt = val.lastIndexOf("@");
    if (lastAt !== -1 && lastAt === val.length - 1) {
      setShowMentions(true); setMentionQuery("");
    } else if (lastAt !== -1 && val.slice(lastAt + 1).match(/^\w*$/)) {
      setShowMentions(true); setMentionQuery(val.slice(lastAt + 1).toLowerCase());
    } else {
      setShowMentions(false);
    }
    sendTyping(true);
    clearTimeout(typingTimer.current);
    typingTimer.current = setTimeout(() => sendTyping(false), 2000);
  };

  const insertMention = (user) => {
    const lastAt = text.lastIndexOf("@");
    setText(text.slice(0, lastAt) + `@${user.name.split(" ")[0]} `);
    setShowMentions(false);
    inputRef.current?.focus();
  };

  const sendMessage = async (extra = {}) => {
    if (!text.trim() && !extra.file_data) return;
    setSending(true);
    sendTyping(false);
    clearTimeout(typingTimer.current);
    try {
      await authFetch(`${BASE}/chat/messages`, {
        method: "POST",
        credentials: "include",
        body: JSON.stringify({
          channel, from_id: userId, from_name: currentUser.name,
          from_avatar: currentUser.avatar,
          to_id: channel === "general" ? null : channel,
          text: text.trim(), ...extra,
        }),
      });
      setText(""); setShowMentions(false);
    } catch {}
    setSending(false);
  };

  const sendReaction = async (msgId, emoji) => {
    try {
      await authFetch(`${BASE}/chat/reactions`, {
        method: "POST",
        body: JSON.stringify({ msg_id: msgId, emoji, from_id: userId, channel }),
      });
      setMessages(prev => prev.map(m => {
        if (m.id !== msgId) return m;
        const reactions = { ...(m.reactions || {}) };
        if (!reactions[emoji]) reactions[emoji] = [];
        if (reactions[emoji].includes(userId)) {
          reactions[emoji] = reactions[emoji].filter(id => id !== userId);
          if (reactions[emoji].length === 0) delete reactions[emoji];
        } else {
          reactions[emoji] = [...reactions[emoji], userId];
        }
        return { ...m, reactions };
      }));
    } catch {}
  };

  const handleFile = async (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (e) => {
      await sendMessage({ file_name: file.name, file_data: e.target.result.split(",")[1], file_type: file.type });
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e) => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files[0]); };

  const mentionedUsers = USERS.filter(u => u.id !== userId && u.name.toLowerCase().includes(mentionQuery));
  const dmUser = USERS.find(u => u.id === channel);

  const selectChannel = (ch) => {
    setChannel(ch);
    setMessages([]);
    if (isMobile) setMobilePane("chat");
  };

  const ChannelList = () => (
    <div style={{
      width: isMobile ? "100%" : 200,
      background: "var(--sidebar)",
      borderRight: isMobile ? "none" : "0.5px solid var(--border)",
      display: "flex", flexDirection: "column", flexShrink: 0,
      height: "100%",
    }}>
      <div style={S.chanSection}>
        <div style={S.chanLabel}>Canales</div>
        <button onClick={() => selectChannel("general")}
          style={{ ...S.chanItem, ...(channel === "general" ? S.chanActive : {}) }}>
          <span style={{ color: "var(--goldDim)" }}>#</span> General
        </button>
      </div>
      <div style={S.chanSection}>
        <div style={S.chanLabel}>Mensajes directos</div>
        {USERS.filter(u => u.id !== userId).map(u => (
          <button key={u.id} onClick={() => selectChannel(u.id)}
            style={{ ...S.chanItem, ...(channel === u.id ? S.chanActive : {}) }}>
            <Avatar avatar={u.avatar} color={u.color} size={28} online={online.includes(u.id)} />
            <span style={{ fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {u.name.split(" ")[0]}
            </span>
          </button>
        ))}
      </div>
      <div style={{ marginTop: "auto", padding: "12px 16px", borderTop: "0.5px solid var(--border)" }}>
        <div style={S.chanLabel}>En línea · {online.length}</div>
      </div>
    </div>
  );

  const ChatPane = () => (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minWidth: 0 }}
      onDragOver={e => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}>
      <div style={{ ...S.chatHeader, padding: isMobile ? "12px 16px" : "16px 24px" }}>
        {isMobile && (
          <button onClick={() => setMobilePane("list")} style={{ background: "none", border: "none", color: "var(--goldDim)", cursor: "pointer", padding: "0 8px 0 0", fontSize: 22, display: "flex", alignItems: "center" }}>
            ‹
          </button>
        )}
        {channel === "general"
          ? <><span style={{ color: "var(--goldDim)", fontSize: 18 }}>#</span>
              <span style={S.chatTitle}>General</span>
              {!isMobile && <span style={S.chatSub}>Canal del equipo · Objetiva Broker</span>}</>
          : <><Avatar avatar={dmUser?.avatar} color={dmUser?.color} size={28} online={online.includes(channel)} />
              <span style={S.chatTitle}>{isMobile ? dmUser?.name.split(" ")[0] : dmUser?.name}</span>
              {!isMobile && <span style={S.chatSub}>{online.includes(channel) ? "En línea" : "Desconectado"}</span>}</>
        }
      </div>
      <div style={S.msgArea}>
        {dragging && (
          <div style={S.dropZone}>
            <Icon d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" size={32} />
            <div style={{ marginTop: 8, fontFamily: "Plus Jakarta Sans, sans-serif", letterSpacing: 2 }}>Suelta el archivo aquí</div>
          </div>
        )}
        {messages.length === 0 && !dragging && (
          <div style={S.emptyChat}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>💬</div>
            <div style={{ fontFamily: "Syne, sans-serif", fontSize: 11, letterSpacing: "0.12em", color: "var(--mute)", textTransform: "uppercase" }}>
              {channel === "general" ? "Inicio del canal General" : `Conversación con ${dmUser?.name}`}
            </div>
          </div>
        )}
        {messages.map((msg, i) => (
          <MessageBubble key={msg.id || i} msg={msg} isOwn={msg.from_id === userId}
            onReact={sendReaction} currentUserId={userId} isMobile={isMobile} />
        ))}
        {typingUsers.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 16px" }}>
            <div style={{ display: "flex", gap: 3 }}>
              {[0,1,2].map(i => (
                <div key={i} style={{ width: 5, height: 5, borderRadius: "50%", background: "var(--goldDim)",
                  animation: `bounce 1.2s ${i * 0.2}s infinite` }} />
              ))}
            </div>
            <span style={{ fontFamily: "Syne, sans-serif", fontSize: 10, letterSpacing: "0.12em", color: "var(--mute)", textTransform: "uppercase" }}>
              {typingUsers.join(", ")} {typingUsers.length === 1 ? "está escribiendo" : "están escribiendo"}...
            </span>
          </div>
        )}
        <div ref={bottomRef} />
      </div>
      <div style={{ position: "relative" }}>
        {showMentions && mentionedUsers.length > 0 && (
          <div style={{ ...S.mentionBox, left: isMobile ? 8 : 20, right: isMobile ? 8 : 20 }}>
            <div style={{ fontFamily: "Syne, sans-serif", fontSize: 9, letterSpacing: "0.15em", color: "var(--mute)", textTransform: "uppercase", padding: "8px 12px 4px" }}>Mencionar</div>
            {mentionedUsers.map(u => (
              <button key={u.id} onClick={() => insertMention(u)} style={S.mentionItem}>
                <Avatar avatar={u.avatar} color={u.color} size={22} />
                <span style={{ fontSize: 12, color: "var(--text)", fontFamily: "Syne, sans-serif" }}>{u.name}</span>
              </button>
            ))}
          </div>
        )}
        <div style={{ ...S.inputArea, padding: isMobile ? "10px 12px" : "12px 20px" }}>
          <button onClick={() => fileRef.current?.click()} style={S.attachBtn} title="Adjuntar">
            <Icon d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" size={15} />
          </button>
          <input ref={fileRef} type="file" style={{ display: "none" }} onChange={e => handleFile(e.target.files[0])} />
          <input ref={inputRef} value={text} onChange={handleTextChange}
            onKeyDown={e => {
              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
              if (e.key === "Escape") setShowMentions(false);
            }}
            placeholder={channel === "general" ? "Mensaje al equipo..." : `Mensaje a ${dmUser?.name.split(" ")[0]}...`}
            style={{ ...S.msgInput, fontSize: isMobile ? 14 : 13 }}
          />
          <button onClick={() => sendMessage()} disabled={sending || !text.trim()} style={S.sendBtn}>
            <Icon d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" size={15} />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ display: "flex", height: "100%", overflow: "hidden", minHeight: 0 }}>
      {isMobile ? (
        mobilePane === "list" ? <ChannelList /> : <ChatPane />
      ) : (
        <><ChannelList /><ChatPane /></>
      )}
      <style>{`
        @keyframes bounce {
          0%, 60%, 100% { transform: translateY(0); }
          30% { transform: translateY(-4px); }
        }
      `}</style>
    </div>
  );
}

function MessageBubble({ msg, isOwn, onReact, currentUserId, isMobile }) {
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const isSystem = msg.type === "system";
  const user = USERS.find(u => u.id === msg.from_id);

  const renderText = (text) => {
    if (!text) return null;
    const parts = text.split(/(@\w+)/g);
    return parts.map((part, i) =>
      part.startsWith("@")
        ? <span key={i} style={{ color: "var(--gold)", fontWeight: 600 }}>{part}</span>
        : part
    );
  };

  if (isSystem) return (
    <div style={{ textAlign: "center", padding: "8px 0" }}>
      <span style={{ fontFamily: "Syne, sans-serif", fontSize: 10, letterSpacing: "0.12em", color: "var(--mute)", textTransform: "uppercase" }}>{msg.text}</span>
    </div>
  );

  return (
    <div style={{ display: "flex", gap: isMobile ? 8 : 10, padding: isMobile ? "4px 12px" : "4px 20px",
      flexDirection: isOwn ? "row-reverse" : "row", alignItems: "flex-start",
      marginBottom: 2 }}>
      <Avatar avatar={msg.from_avatar || user?.avatar || "??"} color={user?.color || "var(--gold)"} size={isMobile ? 26 : 30} />
      <div style={{ maxWidth: isMobile ? "78%" : "65%", display: "flex", flexDirection: "column",
        alignItems: isOwn ? "flex-end" : "flex-start" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4,
          flexDirection: isOwn ? "row-reverse" : "row" }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: "var(--goldDim)", fontFamily: "Syne, sans-serif", letterSpacing: "0.04em" }}>
            {isOwn ? "Tú" : msg.from_name}
          </span>
          <span style={{ fontFamily: "Syne, sans-serif", fontSize: 9, color: "var(--mute)", letterSpacing: "0.06em" }}>
            {new Date(msg.created_at).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}
          </span>
        </div>
        <div style={{ position: "relative" }}>
          {msg.text && (
            <div style={{
              background: isOwn ? "var(--goldDim)" : "transparent",
              border: isOwn ? `0.5px solid var(--gold)` : "none",
              borderRadius: isOwn ? "12px 4px 12px 12px" : "0",
              padding: isOwn ? (isMobile ? "7px 10px" : "8px 14px") : "0",
              fontSize: isMobile ? 14 : 13,
              color: isOwn ? "var(--bgApp)" : "var(--text)",
              lineHeight: 1.55,
            }}>
              {renderText(msg.text)}
            </div>
          )}
          {msg.file_data && <FileAttachment msg={msg} isOwn={isOwn} />}
        </div>
        {msg.reactions && Object.keys(msg.reactions).length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 }}>
            {Object.entries(msg.reactions).map(([emoji, users]) => (
              <button key={emoji} onClick={() => onReact(msg.id, emoji)}
                style={{ display: "flex", alignItems: "center", gap: 3, padding: "1px 6px", borderRadius: 8,
                  background: users.includes(currentUserId) ? "var(--lift)" : "transparent",
                  border: `0.5px solid ${users.includes(currentUserId) ? "var(--goldDim)" : "var(--border)"}`,
                  cursor: "pointer", fontSize: 11, color: "var(--mute)" }}>
                {emoji} <span style={{ fontSize: 11, color: "var(--sec)" }}>{users.length}</span>
              </button>
            ))}
            <button onClick={() => setShowEmojiPicker(p => !p)}
              style={{ padding: "1px 6px", borderRadius: 8, background: "transparent",
                border: "0.5px solid var(--border)", cursor: "pointer", fontSize: 11, color: "var(--mute)" }}>+</button>
          </div>
        )}
        {msg.id && (
          <div style={{ display: "flex", gap: 4, marginTop: 4, flexWrap: "wrap" }}>
            {(!msg.reactions || Object.keys(msg.reactions).length === 0) && !showEmojiPicker && (
              <button onClick={() => setShowEmojiPicker(p => !p)}
                style={{ padding: "2px 8px", borderRadius: 12, background: "none",
                  border: "0.5px solid var(--border)", cursor: "pointer", fontSize: 11, color: "var(--mute)",
                  fontFamily: "Syne, sans-serif", letterSpacing: "0.06em" }}>
                + reacción
              </button>
            )}
            {showEmojiPicker && (
              <div style={{ display: "flex", gap: 4, background: "var(--card)",
                border: "0.5px solid var(--border)", borderRadius: "8px", padding: "4px 8px" }}>
                {EMOJIS.map(e => (
                  <button key={e} onClick={() => { onReact(msg.id, e); setShowEmojiPicker(false); }}
                    style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16 }}>
                    {e}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function FileAttachment({ msg, isOwn }) {
  const isImage = msg.file_type?.startsWith("image/");
  const dataUrl = `data:${msg.file_type};base64,${msg.file_data}`;
  return (
    <div style={{ marginTop: msg.text ? 6 : 0,
      background: isOwn ? "var(--lift)" : "var(--card)",
      border: `0.5px solid ${isOwn ? "var(--goldDim)" : "var(--border)"}`,
      borderRadius: "8px", overflow: "hidden", maxWidth: 280 }}>
      {isImage
        ? <img src={dataUrl} alt={msg.file_name} style={{ width: "100%", display: "block", maxHeight: 200, objectFit: "cover" }} />
        : <a href={dataUrl} download={msg.file_name}
            style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", textDecoration: "none" }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
            </svg>
            <div>
              <div style={{ fontSize: 12, color: "var(--text)", fontWeight: 600, fontFamily: "Syne, sans-serif" }}>{msg.file_name}</div>
              <div style={{ fontFamily: "Syne, sans-serif", fontSize: 9, color: "var(--goldDim)", letterSpacing: "0.08em", textTransform: "uppercase", marginTop: 2 }}>Descargar</div>
            </div>
          </a>
      }
    </div>
  );
}

const S = {
  chanSection: { padding: "20px 0 8px" },
  chanLabel:   { fontFamily: "Syne, sans-serif", fontSize: 9, letterSpacing: "0.18em",
                 color: "var(--goldDim)", textTransform: "uppercase", padding: "0 16px", marginBottom: 6 },
  chanItem:    { display: "flex", alignItems: "center", gap: 8, width: "100%",
                 padding: "10px 16px", background: "none", border: "none",
                 color: "var(--text)", cursor: "pointer", fontSize: 13, fontWeight: 500,
                 textAlign: "left", borderLeft: "2px solid transparent" },
  chanActive:  { background: "var(--hover)", color: "var(--gold)", borderLeftColor: "var(--gold)" },
  chatHeader:  { display: "flex", alignItems: "center", gap: 12, padding: "12px 20px",
                 borderBottom: "0.5px solid var(--border)", flexShrink: 0 },
  chatTitle:   { fontSize: 15, fontWeight: 700, color: "var(--text)", fontFamily: "Syne, sans-serif" },
  chatSub:     { fontFamily: "Syne, sans-serif", fontSize: 10, letterSpacing: "0.12em",
                 color: "var(--mute)", textTransform: "uppercase", marginLeft: 4 },
  msgArea:     { flex: 1, overflow: "auto", padding: "8px 0 4px", display: "flex",
                 flexDirection: "column", gap: 1, position: "relative", justifyContent: "flex-end" },
  emptyChat:   { display: "flex", flexDirection: "column", alignItems: "center",
                 justifyContent: "center", flex: 1, padding: 40 },
  dropZone:    { position: "absolute", inset: 0, background: "rgba(0,0,0,0.2)",
                 border: "2px dashed var(--goldDim)", display: "flex",
                 flexDirection: "column", alignItems: "center", justifyContent: "center",
                 color: "var(--goldDim)", zIndex: 10, backdropFilter: "blur(4px)" },
  inputArea:   { display: "flex", alignItems: "center", gap: 10,
                 padding: "10px 16px", borderTop: "0.5px solid var(--border)", flexShrink: 0 },
  msgInput:    { flex: 1, background: "var(--card)", border: "0.5px solid var(--border)",
                 borderRadius: "8px", padding: "11px 16px", color: "var(--text)",
                 fontFamily: "Syne, sans-serif", fontSize: 13, outline: "none" },
  attachBtn:   { background: "none", border: "none", color: "var(--goldDim)",
                 cursor: "pointer", padding: 8, display: "flex", alignItems: "center" },
  sendBtn:     { background: "none", border: "1px solid var(--gold)", color: "var(--gold)",
                 borderRadius: "8px", padding: "8px 14px", cursor: "pointer",
                 display: "flex", alignItems: "center" },
  mentionBox:  { position: "absolute", bottom: "100%", left: 20, right: 20,
                 background: "var(--card)", border: "0.5px solid var(--border)",
                 borderRadius: "8px", zIndex: 50, marginBottom: 4 },
  mentionItem: { display: "flex", alignItems: "center", gap: 10, width: "100%",
                 padding: "8px 12px", background: "none", border: "none",
                 cursor: "pointer", textAlign: "left", fontFamily: "Syne, sans-serif" },
};
